import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import awsS3Service from '../services/awsS3Service';
import './DailyTaskReport.css';

interface DailyTask {
  id: string;
  date: string;
  particulars: string;
  remarks: string;
  userId: string;
  userName: string;
  createdAt: Date;
  updatedAt: Date;
}

interface SimpleDailyTaskEditorProps {
  isAdmin?: boolean;
  selectedUserId?: string;
  selectedUserName?: string;
}

const SimpleDailyTaskEditor: React.FC<SimpleDailyTaskEditorProps> = ({ 
  isAdmin = false, 
  selectedUserId, 
  selectedUserName 
}) => {
  const { user } = useAuth();
  const [dailyTasks, setDailyTasks] = useState<DailyTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingTask, setEditingTask] = useState<Partial<DailyTask>>({});
  const [lastLoaded, setLastLoaded] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Determine which user's tasks to show
  const targetUserId = isAdmin ? selectedUserId : user?.uid;
  const targetUserName = isAdmin ? selectedUserName : user?.displayName || 'You';

  // Load daily tasks from AWS S3
  const loadDailyTasks = useCallback(async () => {
    if (!targetUserId) return;

    try {
      setLoading(true);
      
      console.log(`🔄 Loading daily tasks for user: ${targetUserId}`);
      
      // Load from AWS S3
      const fileName = `daily-tasks-${targetUserId}.json`;
      const s3Data = await awsS3Service.downloadJsonData(fileName);
      
      if (s3Data) {
        const tasks = JSON.parse(s3Data);
        // Sort tasks by date (newest first)
        const sortedTasks = tasks.sort((a: DailyTask, b: DailyTask) => {
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        });
        console.log(`✅ Loaded ${sortedTasks.length} daily tasks from S3`);
        console.log('📋 Task data:', sortedTasks.map((t: DailyTask) => ({ date: t.date, particulars: t.particulars })));
        setDailyTasks(sortedTasks);
        setLastLoaded(new Date());
        setHasUnsavedChanges(false); // Reset unsaved changes flag
      } else {
        // No data exists yet, start with empty array
        console.log('ℹ️ No daily tasks found in S3');
        setDailyTasks([]);
        setLastLoaded(new Date());
      }
    } catch (error) {
      console.error('❌ Error loading daily tasks from S3:', error);
      setDailyTasks([]);
      setLastLoaded(new Date());
    } finally {
      setLoading(false);
    }
  }, [targetUserId]);

  // Load data on mount and when targetUserId changes
  useEffect(() => {
    loadDailyTasks();
  }, [loadDailyTasks]);

  // Save daily tasks to AWS S3
  const saveDailyTasksToS3 = useCallback(async (tasks: DailyTask[]) => {
    if (!targetUserId || isAdmin) return;

    try {
      setLoading(true);
      
      // Filter out any tasks with empty particulars
      const validTasks = tasks.filter(t => t.particulars && t.particulars.trim() !== '');
      
      console.log('💾 Saving daily tasks to S3:', validTasks.length, 'tasks (filtered from', tasks.length, ')');
      console.log('📋 Task data being saved:', validTasks.map((t: DailyTask) => ({ date: t.date, particulars: t.particulars })));
      
      const fileName = `daily-tasks-${targetUserId}.json`;
      const jsonData = JSON.stringify(validTasks, null, 2);
      
      console.log('📤 Uploading to S3...');
      await awsS3Service.uploadJsonData(fileName, jsonData);
      
      console.log('✅ Daily tasks saved to AWS S3 successfully');
      setHasUnsavedChanges(false); // Clear unsaved changes flag
      
      // Reload data to confirm save
      console.log('🔄 Reloading data to confirm save...');
      await loadDailyTasks();
      
      alert('✅ Daily tasks saved successfully to AWS S3!\n\nData has been reloaded from S3 to confirm.');
    } catch (error) {
      console.error('❌ Error saving daily tasks to S3:', error);
      alert('Error saving daily tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [targetUserId, isAdmin, loadDailyTasks]);

  // Add new row
  const handleAddRow = () => {
    const newTask: DailyTask = {
      id: `task-${Date.now()}`,
      date: new Date().toISOString().split('T')[0],
      particulars: '',
      remarks: '',
      userId: targetUserId || '',
      userName: targetUserName || 'Unknown User',
      createdAt: new Date(),
      updatedAt: new Date()
    };
    setDailyTasks([...dailyTasks, newTask]);
    setEditingIndex(dailyTasks.length);
    setEditingTask(newTask);
    setHasUnsavedChanges(true); // Mark as having unsaved changes
  };

  // Start editing a row
  const handleEditRow = (index: number) => {
    setEditingIndex(index);
    setEditingTask({ ...dailyTasks[index] });
  };

  // Save edited row
  const handleSaveRow = () => {
    if (editingIndex === null) return;

    // Validate that particulars is filled
    if (!editingTask.particulars || editingTask.particulars.trim() === '') {
      alert('⚠️ Please fill in the "Particulars" field before saving!');
      return;
    }

    const updatedTasks = [...dailyTasks];
    updatedTasks[editingIndex] = {
      ...updatedTasks[editingIndex],
      ...editingTask,
      updatedAt: new Date()
    };
    
    console.log('✅ Row saved locally:', { date: editingTask.date, particulars: editingTask.particulars });
    setDailyTasks(updatedTasks);
    setEditingIndex(null);
    setEditingTask({});
    setHasUnsavedChanges(true); // Mark as having unsaved changes
  };

  // Cancel editing
  const handleCancelEdit = () => {
    // If it's a new row that was never saved, remove it
    if (editingIndex === dailyTasks.length - 1 && !dailyTasks[editingIndex].particulars) {
      setDailyTasks(dailyTasks.slice(0, -1));
    }
    setEditingIndex(null);
    setEditingTask({});
  };

  // Delete row
  const handleDeleteRow = (index: number) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      const updatedTasks = dailyTasks.filter((_, i) => i !== index);
      setDailyTasks(updatedTasks);
      setHasUnsavedChanges(true); // Mark as having unsaved changes
    }
  };

  // Save all to S3
  const handleSaveToS3 = async () => {
    console.log('🎯 Starting save process...');
    console.log('📋 Current state has', dailyTasks.length, 'tasks');
    console.log('📋 Tasks to save:', dailyTasks.map((t: DailyTask) => ({ date: t.date, particulars: t.particulars })));
    await saveDailyTasksToS3(dailyTasks);
  };

  return (
    <div className="simple-daily-task-editor">
      <div className="editor-header">
        <div className="header-top">
          <h3>📊 Daily Tasks - {targetUserName}</h3>
          {lastLoaded && isAdmin && (
            <span className="last-loaded">
              Last loaded: {lastLoaded.toLocaleTimeString()}
            </span>
          )}
        </div>
        <div className="editor-instructions">
          <p>📝 <strong>Instructions:</strong> Add your daily tasks below.</p>
          <p>📅 <strong>Date:</strong> Enter date in YYYY-MM-DD format</p>
          <p>📋 <strong>Particulars:</strong> Describe what you did (required)</p>
          <p>💭 <strong>Remarks:</strong> Additional notes (optional)</p>
          {!isAdmin && (
            <p>💾 <strong>Storage:</strong> Data is saved to AWS S3 cloud storage</p>
          )}
        </div>
        <div className="editor-actions">
          {!isAdmin ? (
            <>
              <button onClick={handleAddRow} className="add-row-btn" disabled={loading}>
                ➕ Add New Task
              </button>
              <button 
                onClick={handleSaveToS3} 
                className={`save-to-s3-btn ${hasUnsavedChanges ? 'unsaved-changes' : ''}`}
                disabled={loading}
              >
                {loading ? 'Saving...' : hasUnsavedChanges ? '💾 Save  (Unsaved Changes!)' : '💾 Save'}
              </button>
              {hasUnsavedChanges && (
                <span className="unsaved-warning">
                  ⚠️ You have unsaved changes!
                </span>
              )}
            </>
          ) : (
            <button onClick={loadDailyTasks} className="refresh-btn" disabled={loading}>
              {loading ? '🔄 Loading...' : '🔄 Refresh Data'}
            </button>
          )}
        </div>
      </div>

      <div className="excel-table-container">
        <table className="excel-table">
          <thead>
            <tr>
              <th style={{ width: '150px' }}>Date</th>
              <th style={{ width: '40%' }}>Particulars</th>
              <th style={{ width: '40%' }}>Remarks</th>
              {!isAdmin && <th style={{ width: '120px' }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {dailyTasks.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? 3 : 4} style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  {loading ? 'Loading...' : 'No daily tasks yet. Click "Add New Task" to get started.'}
                </td>
              </tr>
            ) : (
              dailyTasks.map((task, index) => (
                <tr key={task.id}>
                  {editingIndex === index ? (
                    <>
                      <td>
                        <input
                          type="date"
                          value={editingTask.date || ''}
                          onChange={(e) => setEditingTask({ ...editingTask, date: e.target.value })}
                          className="excel-input"
                        />
                      </td>
                      <td>
                        <textarea
                          value={editingTask.particulars || ''}
                          onChange={(e) => setEditingTask({ ...editingTask, particulars: e.target.value })}
                          className="excel-textarea"
                          rows={2}
                          placeholder="What did you do?"
                        />
                      </td>
                      <td>
                        <textarea
                          value={editingTask.remarks || ''}
                          onChange={(e) => setEditingTask({ ...editingTask, remarks: e.target.value })}
                          className="excel-textarea"
                          rows={2}
                          placeholder="Additional notes..."
                        />
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button onClick={handleSaveRow} className="save-btn-small">✓</button>
                          <button onClick={handleCancelEdit} className="cancel-btn-small">✗</button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{task.date}</td>
                      <td>{task.particulars}</td>
                      <td>{task.remarks}</td>
                      {!isAdmin && (
                        <td>
                          <div className="action-buttons">
                            <button onClick={() => handleEditRow(index)} className="edit-btn-small">✏️</button>
                            <button onClick={() => handleDeleteRow(index)} className="delete-btn-small">🗑️</button>
                          </div>
                        </td>
                      )}
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Saving daily tasks to AWS S3...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleDailyTaskEditor;

